import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { fmtAgo } from '../lib/labels.js';

/**
 * MarketingCycle — the operational loop Markaii runs continuously, shown as a
 * cycle so the owner can see the machine's rhythm: where it just was, and what's
 * next. Distinct from the LoopStatus health table (8-stage SOSTAC config check).
 *
 * Data comes from GET /projects/:key/cycle — five phases (Generate → Approve →
 * Publish → Sense → Iterate), each with what last happened and what's next.
 * fmtAgo (date-fns addSuffix) renders both past ("4 hours ago") and future
 * ("in 2 days"), so the same formatter labels Last and Next.
 */

const PHASE_ICON = {
  generate: '✎',
  approve: '✓',
  publish: '↑',
  sense: '📊',
  iterate: '↻',
};

const STATUS_TITLE = {
  active: 'Running',
  attention: 'Needs you',
  waiting: 'Waiting on data',
  idle: 'Not set up yet',
};

export function MarketingCycle({ projectKey }) {
  const q = useQuery({
    queryKey: ['cycle', projectKey],
    queryFn: ({ signal }) => apiGet(`/projects/${encodeURIComponent(projectKey)}/cycle`, { signal }),
    staleTime: 30_000,
  });

  return (
    <section className="post-section marketing-cycle overview-card">
      <div className="section-h-row">
        <h2>Marketing cycle</h2>
        <span className="muted small">the rhythm Markaii runs — continuously, not a daily batch</span>
      </div>
      <p className="muted small cycle-lead">
        Markaii works as a continuous loop. Each pillar drafts on its own schedule, you approve, approved
        posts publish to connected platforms, engagement flows back every few hours, and the plan re-tunes
        monthly from what worked — then it starts over.
      </p>

      {q.isLoading && <div className="loading">Loading cycle…</div>}
      {q.isError && <div className="error">Couldn’t load cycle: {q.error.message}</div>}

      {q.data && (
        <>
          <ol className="cycle-track">
            {q.data.phases.map((ph) => (
              <li key={ph.key} className={`cycle-phase cycle-phase-${ph.status}`}>
                <div className="cycle-phase-head">
                  <span className="cycle-phase-icon" aria-hidden="true">{PHASE_ICON[ph.key] || '•'}</span>
                  <span className="cycle-phase-name">{ph.name}</span>
                  <span
                    className={`cycle-dot cycle-dot-${ph.status}`}
                    title={STATUS_TITLE[ph.status] || ph.status}
                    aria-label={STATUS_TITLE[ph.status] || ph.status}
                  />
                </div>
                <p className="cycle-phase-blurb">{ph.blurb}</p>
                <p className="cycle-phase-cadence">{ph.cadence}</p>
                {ph.note && <p className="cycle-phase-note">{ph.note}</p>}
                <dl className="cycle-phase-times">
                  {ph.last && (
                    <div className="cycle-time">
                      <dt>Last</dt>
                      <dd>{ph.last.label} · {fmtAgo(ph.last.at)}</dd>
                    </div>
                  )}
                  {ph.next && (
                    <div className="cycle-time cycle-time-next">
                      <dt>Next</dt>
                      <dd>{ph.next.label} · {fmtAgo(ph.next.at)}</dd>
                    </div>
                  )}
                </dl>
              </li>
            ))}
          </ol>
          <p className="cycle-loop-back">↻ …then back to <b>Generate</b> — the loop repeats.</p>
        </>
      )}
    </section>
  );
}
